import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

// In dev, leave VITE_API_BASE unset and the Vite proxy in vite.config.js forwards
// /api to your local PHP server. In production, set VITE_API_BASE to the deployed
// api/index.php URL (see .env.example).
const API_BASE = import.meta.env.VITE_API_BASE || "https://acrwork.taskone.xyz/adminw/index.php";
const TOKEN_KEY = "ghorami_admin_token";
const NAME_KEY = "ghorami_admin_name";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => {
    try { return sessionStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
  });
  const [name, setName] = useState(() => {
    try { return sessionStorage.getItem(NAME_KEY) || ""; } catch { return ""; }
  });
  const [toast, setToast] = useState("");

  const say = useCallback((msg) => {
    setToast(msg);
    const id = setTimeout(() => setToast(""), 4500);
    return () => clearTimeout(id);
  }, []);

  const login = useCallback((t, n) => {
    try { sessionStorage.setItem(TOKEN_KEY, t); sessionStorage.setItem(NAME_KEY, n || ""); } catch { /* private mode */ }
    setToken(t); setName(n || "");
  }, []);

  const logout = useCallback(() => {
    try { sessionStorage.removeItem(TOKEN_KEY); sessionStorage.removeItem(NAME_KEY); } catch { /* noop */ }
    setToken(""); setName("");
  }, []);

  // The single fetch wrapper every page uses. `r` is the API action (?r=...),
  // `params` become query-string filters, `body` (POST only) is sent as JSON.
  const api = useCallback(async (r, { method = "GET", params = {}, body } = {}) => {
    const qs = new URLSearchParams({
      r,
      ...Object.fromEntries(Object.entries(params).filter(([, v]) => v !== "" && v != null)),
    });
    let res;
    try {
      res = await fetch(`${API_BASE}?${qs}`, {
        method,
        headers: { "Content-Type": "application/json", ...(token ? { "X-Auth-Token": token } : {}) },
        body: body ? JSON.stringify(body) : undefined,
      });
    } catch {
      throw new Error("Could not reach the server. Check your connection and try again.");
    }
    const json = await res.json().catch(() => ({ error: "The server sent an unreadable response." }));
    if (res.status === 401 && token) logout();
    if (!res.ok) throw new Error(json.error || "Request failed.");
    return json;
  }, [token, logout]);

  // Convenience wrapper for POST actions that also shows a toast on success/failure.
  const act = useCallback(async (r, body, msg) => {
    try {
      const result = await api(r, { method: "POST", body });
      if (msg) say(msg);
      return result;
    } catch (e) {
      say(e.message);
      return null;
    }
  }, [api, say]);

  const value = useMemo(() => ({ token, name, login, logout, api, act, toast, say }),
    [token, name, login, logout, api, act, toast, say]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

// Fetches `r` with `params` on mount and whenever they change; call the returned
// reload() to refetch (e.g. after a save). Params are compared by JSON value so
// passing a fresh object each render is fine.
export function useLoad(r, params = {}) {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  const key = JSON.stringify(params);

  useEffect(() => {
    if (!r) return;
    let live = true;
    setData(null);
    api(r, { params: JSON.parse(key) })
      .then((x) => live && setData(x))
      .catch((e) => live && setData({ error: e.message }));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r, key, tick]);

  return [data, () => setTick((x) => x + 1)];
}
